import { useMemoizedFn } from 'ahooks';
import { isEmpty, uniq, uniqueId } from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dialog, Loading, MessagePlugin, Tree } from 'tdesign-react';
import { isVoid } from '@/utils/common';
import { getPermissions, grantPermissions } from '@/services/roleService';
import ModalHeader from '@/components/Header/Modal';

// 以下暂不处理关联性

/**
 * 根据节点 ID 在树数组中获取节点。
 *
 * @param {Array} treeArray - 包含树节点的数组。
 * @param {number} targetId - 目标节点的 ID。
 * @returns {object|null} 找到的节点对象，如果未找到则返回 null。
 */
function findTreeNodeByIdInArray(treeArray, targetId) {
  for (const rootNode of treeArray) {
    if (rootNode.id === targetId) {
      return rootNode;
    }
    if (rootNode.children) {
      const foundNode = findTreeNodeByIdInArray(rootNode.children, targetId);
      if (foundNode) {
        return foundNode;
      }
    }
  }

  return null;
}

// 剔除所有父节点id，同时剔除无用节点，添加仅展示，并添加辅助节点
const cleanPermissionIds = (idArray, treeData) => {
  const extra = [];
  const filtered = idArray.filter((id) => {
    const currentNode = findTreeNodeByIdInArray(treeData, id);
    if (currentNode) {
      if (!isEmpty(currentNode.children)) {
        // 先添加辅助节点
        const firstChildId = currentNode.children[0].id;
        if (`${firstChildId}`.indexOf('$helper-') > -1) {
          extra.push(firstChildId); // （仅展示）的id
        }
        return false;
      } else {
        // 过滤只要子节点
        return true;
      }
    }
    return false;
  });

  return [...filtered, ...extra];
};

const getAllParentNodeIds = (treeData, targetId) => {
  const result = [];
  function findParentNodeId(tree, targetId) {
    const currentNode = findTreeNodeByIdInArray(tree, targetId);

    if (currentNode) {
      const { parentId } = currentNode;
      if (!isVoid(parentId)) {
        result.unshift(parentId);
        findParentNodeId(tree, parentId);
      }
    }
  }

  findParentNodeId(treeData, targetId);
  return result.concat(targetId);
};

const generateIds = (idArray, treeData) => {
  // 父子节点二维数组，每层包含所有父节点+当前节点id
  const idsArray = [];
  for (const id of idArray) {
    idsArray.push(getAllParentNodeIds(treeData, id));
  }
  return uniq(idsArray.flat());
};

// 加辅助节点
const appendExtraNode = (treeArrayData) => {
  const tempArray = [];
  let existsButton = false; // 存在按钮
  for (const item of treeArrayData) {
    if (item.type === 1) {
      existsButton = true;
    }
    if (!isEmpty(item.children)) {
      item.children = appendExtraNode(item.children);
    }
    tempArray.push({ ...item });
  }
  if (existsButton) {
    tempArray.unshift({
      id: uniqueId('$helper-'),
      label: '页面（展示）',
      parentId: treeArrayData[0].parentId,
    });
  }
  return tempArray;
};

export default function PermissionModal({ visible, handleClose, detail }) {
  const [submitting, setSubmitting] = useState(false);
  const [treeData, setTreeData] = useState();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useMemoizedFn(async () => {
    setLoading(true);
    const {
      data: { assignedPermissions, systemPermissions },
    } = await getPermissions({ roleId: detail.id });
    const treeArrayData = appendExtraNode(systemPermissions);
    setTreeData(treeArrayData);
    // 剔除所有父节点id，同时剔除无用节点，并添加辅助节点id（无子节点选中，但主节点选中情况）
    const noParentPermissions = cleanPermissionIds(assignedPermissions, treeArrayData);
    setPermissions(noParentPermissions);
    setLoading(false);
  });

  useEffect(() => {
    if (visible) {
      loadData();
    } else {
      setTreeData(null);
      setPermissions([]);
    }
  }, [loadData, visible]);

  const handleConfirm = async () => {
    // 生产节点（如子节点选中，则父节点逐层一并选中）并提出$helper-开头
    const ids = generateIds(permissions, treeData);
    const generated = ids.filter((item) => `${item}`.indexOf('$helper-') < 0);
    try {
      setSubmitting(true);
      await grantPermissions({
        roleId: detail.id,
        permissions: generated,
      });
      MessagePlugin.success('操作成功');
      handleClose(true);
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (vals) => {
    setPermissions(vals);
  };

  return (
    <Dialog
      header={<ModalHeader tags={[detail?.name]}>角色授权</ModalHeader>}
      visible={visible}
      onClose={() => handleClose()}
      confirmBtn={{ loading: submitting, content: '确定' }}
      onConfirm={handleConfirm}
      placement='center'
      width={550}
    >
      <Loading size='small' style={{ minHeight: 300 }} loading={loading}>
        <Root>
          {treeData && (
            <Tree
              data={treeData}
              checkable
              keys={{ value: 'id' }}
              value={permissions}
              valueMode='all'
              onChange={handleChange}
            />
          )}
        </Root>
      </Loading>
    </Dialog>
  );
}

const Root = styled.div`
  .t-tree--block-node .t-tree__label {
    flex: 0 0 auto;
  }
`;
